import { type FC } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Col, Row } from "~/common/components/Layout";
import { Button, Stack } from "~/common/mui";
import { CloseBtn } from "~/features/addToCart/components/AtcCloseButton";

import Modal from "~/common/components/Modal";
import { useForm } from "react-hook-form";
import ShareConfigurationsViaEmailPopupForm from "~/features/savedConfigurations/components/ShareConfigurationsViaEmailPopupForm";
import { sendWishlistViaEmail } from "~/features/wishlist/components/modals/ShareWishlistViaEmailPopup/api/shareWishlistViaEmail";
import { useTranslation } from "react-i18next";

interface Props {
  onClose: () => void;
  selectedWishlistName: string;
  selectedWishlistId?: string;
}

const ShareWishlistViaEmailPopup: FC<Props> = ({ onClose, selectedWishlistName, selectedWishlistId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm();

  const onSubmitHandler = handleSubmit(async (values) => {
    const { email, consent, firstName, lastName, note } = values;
    if (email && consent) {
      try {
        const result = await sendWishlistViaEmail({
          selectedWishlistName: selectedWishlistName,
          sharedWishlist: selectedWishlistId!,
          email,
          firstName,
          lastName,
          note,
        });

        if (result.error) {
          setError("email", { message: result.error });
        } else {
          await navigate({
            to: "/sendWishlistViaEmail/confirmation",
            state: { wishlistName: selectedWishlistName },
          });
        }
      } catch {
        setError("email", { message: "generic.error.ajax" });
      }
    }
  });

  return (
    <Modal
      onClose={onClose}
      title={t("page.savedWishlistPopup.shareViaEmail.wishlist", { selectedWishlistName })}
      footerChildren={
        <Row className={"d-flex flex-justify-between flex-align-center w-100"}>
          <Col size={4} className={"d-flex"}>
            <div className={"d-flex flex-align-center"}>{t("mandatory")}</div>
          </Col>
          <Stack direction={"row"} gap={1}>
            <CloseBtn data-test-id="btn-share-wishlist-via-email-popup-cancel" onClick={onClose}>
              {t("cancel")}
            </CloseBtn>
            <Button data-test-id="btn-share-wishlist-via-email-popup-confirm" onClick={onSubmitHandler}>
              {t("page.savedConfigsPopup.shareViaEmail.send")}
            </Button>
          </Stack>
        </Row>
      }
    >
      <Row>
        <Col size={12}>
          <ShareConfigurationsViaEmailPopupForm register={register} control={control} errors={errors} />
        </Col>
      </Row>
    </Modal>
  );
};

export default ShareWishlistViaEmailPopup;
